import React, { useState, useEffect } from 'react';
import { Search, AlertCircle, CheckCircle, Plus, Trash2, Save, Calculator, ClipboardList, User } from 'lucide-react';
import axiosInstance from '../../variables/api/axiosInstance';
import NavBar from '../../components/NavBar';
import SideBar from '../../components/SideBar';
import Footer from '../../components/Footer';
import { toast } from 'react-toastify';

const GestionPaiementSinistreEntitesConstat = () => {
    const [entites, setEntites] = useState([]);
    const [sinistres, setSinistres] = useState([]);
    const [entiteSelectionnee, setEntiteSelectionnee] = useState(null);
    const [tauxRetenue, setTauxRetenue] = useState(5);
    const [dossiersSelectionnes, setDossiersSelectionnes] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchEntite, setSearchEntite] = useState('');
    const [entitesAffichees, setEntitesAffichees] = useState(5);

    // ── Responsable ──────────────────────────────────────────────────────────
    const [responsable, setResponsable] = useState('');
    const [responsableError, setResponsableError] = useState(false);

    // Sélection d'entité — reset synchrone
    const handleSelectEntite = (entite) => {
        setSinistres([]);
        setDossiersSelectionnes([]);
        setSearchTerm('');
        setTauxRetenue(entite.tauxRetenue ?? 5);
        setEntiteSelectionnee(entite);
        setResponsable('');          // reset du responsable à chaque changement d'entité
        setResponsableError(false);
    };

    // Chargement des sinistres après sélection d'entité
    useEffect(() => {
        if (entiteSelectionnee) {
            axiosInstance
                .get(`/api/sinistre/entite-constat/${entiteSelectionnee.entiteConstatId}`)
                .then((response) => {
                    const data = Array.isArray(response.data)
                        ? response.data
                        : response.data?.content
                        ?? response.data?.data
                        ?? response.data?.items
                        ?? [];
                    setSinistres(data);
                })
                .catch((error) => {
                    console.error(error);
                    toast.error("Erreur lors du chargement des sinistres de l'entité.");
                });
        } else {
            setSinistres([]);
            setDossiersSelectionnes([]);
        }
    }, [entiteSelectionnee]);

    // Chargement de toutes les entités de constat
    useEffect(() => {
        axiosInstance
            .get('/api/entite-constat')
            .then((response) => {
                setEntites(response.data);
            })
            .catch((error) => {
                console.error(error);
                toast.error("Erreur lors du chargement des entités de constat.");
            });
    }, []);

    const ajouterDossier = (dossier) => {
        if (!dossiersSelectionnes.find((d) => d.codeId === dossier.codeId)) {
            setDossiersSelectionnes([
                ...dossiersSelectionnes,
                { ...dossier, fraisConstat: 0, fraisAccessoires: 0 },
            ]);
        }
    };

    const retirerDossier = (dossierId) => {
        setDossiersSelectionnes(dossiersSelectionnes.filter((d) => d.codeId !== dossierId));
    };

    const mettreAJourMontant = (dossierId, champ, valeur) => {
        setDossiersSelectionnes(
            dossiersSelectionnes.map((d) =>
                d.codeId === dossierId ? { ...d, [champ]: parseFloat(valeur) || 0 } : d
            )
        );
    };

    const verifierMontant = (dossier) => {
        const total = dossier.fraisConstat + dossier.fraisAccessoires;
        return total <= (dossier.sap ?? Infinity);
    };

    const calculerTotaux = () => {
        const totalFraisConstat = dossiersSelectionnes.reduce((acc, d) => acc + d.fraisConstat, 0);
        const totalFraisAccessoires = dossiersSelectionnes.reduce((acc, d) => acc + d.fraisAccessoires, 0);
        const totalBrut = totalFraisConstat + totalFraisAccessoires;
        const montantRetenue = totalBrut * (tauxRetenue / 100);
        const totalNet = totalBrut - montantRetenue;
        return { totalFraisConstat, totalFraisAccessoires, totalBrut, montantRetenue, totalNet };
    };

    // ── Validation globale : dossiers OK + responsable renseigné ─────────────
    const validerReglement = () => {
        if (dossiersSelectionnes.length === 0) return false;
        if (!responsable?.trim()) return false;
        return dossiersSelectionnes.every((d) => verifierMontant(d));
    };

    // Anti double-clic : verrouille le bouton pendant l'enregistrement.
    const [saving, setSaving] = useState(false);

    const enregistrerReglement = () => {
        if (saving) return;
        if (!responsable?.trim()) {
            setResponsableError(true);
            toast.warning('Veuillez renseigner le nom du premier responsable / commissaire principal.');
            return;
        }
        if (!validerReglement()) return;

        const payload = dossiersSelectionnes.map((d) => ({
            codeId: d.codeId,
            fraisConstat: d.fraisConstat,
            fraisAccessoires: d.fraisAccessoires,
            entiteConstatId: entiteSelectionnee.entiteConstatId,
            responsable: responsable.trim(),   // ← champ ajouté
        }));

        setSaving(true);
        axiosInstance
            .post('/api/decaissement/all-entites-constat', JSON.stringify(payload))
            .then(() => {
                toast.success('Règlement enregistré avec succès');
                setDossiersSelectionnes([]);
                setEntiteSelectionnee(null);
                setResponsable('');
                setResponsableError(false);
            })
            .catch((error) => {
                console.error(error);
                toast.error("Erreur lors de l'enregistrement du règlement.");
            })
            .finally(() => setSaving(false));
    };

    const totaux = calculerTotaux();

    const entitesFiltrees = entites.filter((entite) => {
        const recherche = searchEntite?.toLowerCase() || '';
        return (
            entite?.nom?.toLowerCase().includes(recherche) ||
            entite?.adresse?.toLowerCase().includes(recherche)
        );
    });

    const entitesAfficher = entitesFiltrees.slice(0, entitesAffichees);
    const aAutresEntites = entitesFiltrees.length > entitesAffichees;

    const dossiersDisponibles = sinistres.filter(
        (d) =>
            !dossiersSelectionnes.find((ds) => ds.codeId === d.codeId) &&
            (
                d?.numSinistre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                d?.numeroSinistreM?.toLowerCase().includes(searchTerm.toLowerCase())
            )
    );

    // ── Styles réutilisables ─────────────────────────────────────────────────
    const cardStyle = {
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '24px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    };

    const inputStyle = (hasError = false) => ({
        width: '100%',
        padding: '10px 12px',
        border: `1px solid ${hasError ? '#e74c3c' : '#E0E0E0'}`,
        borderRadius: '6px',
        fontSize: '14px',
        outline: 'none',
        transition: 'border-color 0.2s',
        backgroundColor: hasError ? '#fff8f8' : 'white',
        boxSizing: 'border-box',
    });

    return (
        <div className="layout-wrapper layout-content-navbar">
            <div className="layout-container">
                <SideBar />
                <div className="layout-page">
                    <NavBar />
                    <div className="content-wrapper">
                        <div className="container-xxl flex-grow-1 container-p-y">
                            <div style={{
                                minHeight: '100vh',
                                backgroundColor: '#F9FBF9',
                                padding: '24px',
                                fontFamily: 'system-ui, -apple-system, sans-serif',
                            }}>
                                {/* Header */}
                                <div style={{ ...cardStyle, marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '16px' }}>
                                    <div style={{
                                        width: '52px', height: '52px', borderRadius: '12px',
                                        background: 'linear-gradient(135deg, #2ECC71 0%, #145A32 100%)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        boxShadow: '0 4px 14px rgba(46,204,113,0.35)', flexShrink: 0,
                                    }}>
                                        <ClipboardList size={26} color="white" />
                                    </div>
                                    <div>
                                        <h1 style={{ fontSize: '26px', fontWeight: '700', color: '#333333', margin: '0 0 4px 0' }}>
                                            Nouveau Règlement — Entité de Constat
                                        </h1>
                                        <p style={{ color: '#666', margin: 0, fontSize: '14px' }}>
                                            Sélectionnez une entité de constat et ajoutez les dossiers à régler
                                        </p>
                                    </div>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>

                                    {/* ── Colonne gauche ── */}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>

                                        {/* Sélection entité */}
                                        <div style={cardStyle}>
                                            <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#333333', margin: '0 0 16px 0' }}>
                                                Sélection de l'Entité de Constat
                                            </h2>

                                            <div style={{ position: 'relative', marginBottom: '16px' }}>
                                                <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#999' }} />
                                                <input
                                                    type="text"
                                                    placeholder="Rechercher une entité (nom, adresse)..."
                                                    value={searchEntite}
                                                    onChange={(e) => { setSearchEntite(e.target.value); setEntitesAffichees(5); }}
                                                    style={{ ...inputStyle(), paddingLeft: '40px' }}
                                                />
                                            </div>

                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxHeight: '340px', overflowY: 'auto', paddingRight: '4px' }}>
                                                {entitesAfficher.map((entite) => (
                                                    <div
                                                        key={entite?.codeId}
                                                        onClick={() => handleSelectEntite(entite)}
                                                        style={{
                                                            padding: '16px',
                                                            border: entiteSelectionnee?.codeId === entite.codeId ? '2px solid #2ECC71' : '1px solid #E0E0E0',
                                                            borderRadius: '8px',
                                                            cursor: 'pointer',
                                                            transition: 'all 0.2s',
                                                            backgroundColor: entiteSelectionnee?.codeId === entite?.codeId ? '#A8E6CF20' : 'white',
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            if (entiteSelectionnee?.codeId !== entite?.codeId) {
                                                                e.currentTarget.style.borderColor = '#2ECC71';
                                                                e.currentTarget.style.backgroundColor = '#A8E6CF10';
                                                            }
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            if (entiteSelectionnee?.codeId !== entite?.codeId) {
                                                                e.currentTarget.style.borderColor = '#E0E0E0';
                                                                e.currentTarget.style.backgroundColor = 'white';
                                                            }
                                                        }}
                                                    >
                                                        <div style={{ fontWeight: '600', color: '#333333', marginBottom: '4px' }}>
                                                            {entite.nom}
                                                        </div>
                                                        <div style={{ fontSize: '13px', color: '#666' }}>
                                                            {entite.adresse && `${entite.adresse}`}
                                                        </div>
                                                    </div>
                                                ))}

                                                {entitesFiltrees.length === 0 && (
                                                    <div style={{ textAlign: 'center', padding: '20px', color: '#999', fontSize: '14px' }}>
                                                        Aucune entité trouvée
                                                    </div>
                                                )}

                                                {aAutresEntites && (
                                                    <button
                                                        onClick={() => setEntitesAffichees((prev) => prev + 5)}
                                                        style={{
                                                            padding: '12px', backgroundColor: 'transparent',
                                                            border: '1px dashed #2ECC71', borderRadius: '6px',
                                                            color: '#2ECC71', fontSize: '14px', fontWeight: '500',
                                                            cursor: 'pointer', transition: 'all 0.2s',
                                                        }}
                                                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#A8E6CF20'; }}
                                                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                                                    >
                                                        Afficher plus d'entités ({entitesFiltrees.length - entitesAffichees} restantes)
                                                    </button>
                                                )}
                                            </div>

                                            {/* ── Champ Responsable — affiché après sélection ── */}
                                            {entiteSelectionnee && (
                                                <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #E0E0E0' }}>
                                                    <label style={{
                                                        display: 'flex', alignItems: 'center', gap: '6px',
                                                        fontSize: '14px', fontWeight: '600', color: '#333333', marginBottom: '8px',
                                                    }}>
                                                        <User size={15} style={{ color: '#2ECC71' }} />
                                                        Premier responsable / Commissaire principal
                                                        <span style={{ color: '#e74c3c', marginLeft: '2px' }}>*</span>
                                                    </label>

                                                    <input
                                                        type="text"
                                                        placeholder="Ex : Commissaire AGBEKO Koffi"
                                                        value={responsable}
                                                        onChange={(e) => {
                                                            setResponsable(e.target.value);
                                                            if (e.target.value.trim()) setResponsableError(false);
                                                        }}
                                                        onBlur={() => setResponsableError(!responsable?.trim())}
                                                        style={inputStyle(responsableError)}
                                                    />

                                                    {responsableError && (
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '6px', color: '#e74c3c', fontSize: '12px' }}>
                                                            <AlertCircle size={13} />
                                                            Ce champ est obligatoire pour enregistrer le règlement.
                                                        </div>
                                                    )}

                                                    {responsable?.trim() && !responsableError && (
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '6px', color: '#2ECC71', fontSize: '12px' }}>
                                                            <CheckCircle size={13} />
                                                            {responsable.trim()}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        {/* Liste des dossiers disponibles */}
                                        {entiteSelectionnee && (
                                            <div style={{ ...cardStyle, maxHeight: '500px', display: 'flex', flexDirection: 'column' }}>
                                                <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#333333', margin: '0 0 4px 0' }}>
                                                    Dossiers Disponibles
                                                </h2>
                                                <p style={{ fontSize: '13px', color: '#888', margin: '0 0 16px 0' }}>
                                                    {sinistres.length} dossier{sinistres.length > 1 ? 's' : ''} trouvé
                                                    {sinistres.length > 1 ? 's' : ''} pour cette entité
                                                </p>

                                                <div style={{ position: 'relative', marginBottom: '16px' }}>
                                                    <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#999' }} />
                                                    <input
                                                        type="text"
                                                        placeholder="Rechercher un dossier..."
                                                        value={searchTerm}
                                                        onChange={(e) => setSearchTerm(e.target.value)}
                                                        style={{ ...inputStyle(), paddingLeft: '40px' }}
                                                    />
                                                </div>

                                                <div style={{ flex: 1, overflowY: 'auto' }}>
                                                    {dossiersDisponibles.length === 0 ? (
                                                        <div style={{ textAlign: 'center', padding: '20px', color: '#999', fontSize: '14px' }}>
                                                            {sinistres.length === 0 ? 'Aucun dossier disponible pour cette entité' : 'Tous les dossiers ont été ajoutés'}
                                                        </div>
                                                    ) : (
                                                        dossiersDisponibles.map((dossier) => (
                                                            <div
                                                                key={dossier?.codeId}
                                                                onClick={() => ajouterDossier(dossier)}
                                                                style={{ padding: '12px', border: '1px solid #E0E0E0', borderRadius: '6px', marginBottom: '8px', cursor: 'pointer', transition: 'all 0.2s' }}
                                                                onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#2ECC71'; e.currentTarget.style.backgroundColor = '#A8E6CF10'; }}
                                                                onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#E0E0E0'; e.currentTarget.style.backgroundColor = 'white'; }}
                                                            >
                                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                    <div>
                                                                        <div style={{ fontWeight: '600', color: '#333333', fontSize: '14px' }}>
                                                                            {dossier.numSinistre || dossier.numeroSinistreM}
                                                                        </div>
                                                                        <div style={{ fontSize: '12px', color: '#666' }}>
                                                                            {dossier.type && `${dossier.type} • `}
                                                                            {dossier.sap != null && `SAP : ${dossier.sap.toLocaleString('fr-FR')} FCFA`}
                                                                        </div>
                                                                    </div>
                                                                    <Plus size={18} style={{ color: '#2ECC71' }} />
                                                                </div>
                                                            </div>
                                                        ))
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* ── Colonne droite ── */}
                                    <div style={{ ...cardStyle, display: 'flex', flexDirection: 'column', maxHeight: 'calc(100vh - 200px)' }}>
                                        <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#333333', margin: '0 0 16px 0' }}>
                                            Dossiers à Régler ({dossiersSelectionnes.length})
                                        </h2>

                                        <div style={{ flex: 1, overflowY: 'auto', marginBottom: '16px' }}>
                                            {dossiersSelectionnes.length === 0 ? (
                                                <div style={{ textAlign: 'center', padding: '40px 20px', color: '#999' }}>
                                                    Aucun dossier sélectionné
                                                </div>
                                            ) : (
                                                dossiersSelectionnes.map((dossier) => {
                                                    const estValide = verifierMontant(dossier);
                                                    const total = dossier.fraisConstat + dossier.fraisAccessoires;

                                                    return (
                                                        <div
                                                            key={dossier?.codeId}
                                                            style={{
                                                                padding: '16px',
                                                                border: estValide ? '1px solid #E0E0E0' : '2px solid #e74c3c',
                                                                borderRadius: '8px',
                                                                marginBottom: '12px',
                                                                backgroundColor: estValide ? 'white' : '#ffe5e5',
                                                            }}
                                                        >
                                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
                                                                <div>
                                                                    <div style={{ fontWeight: '600', color: '#333333', fontSize: '14px' }}>
                                                                        {dossier.numSinistre || dossier.numeroSinistreM}
                                                                    </div>
                                                                    <div style={{ fontSize: '12px', color: '#666' }}>
                                                                        {dossier.sap != null && `SAP : ${dossier.sap.toLocaleString('fr-FR')} FCFA`}
                                                                    </div>
                                                                </div>
                                                                <button
                                                                    onClick={() => retirerDossier(dossier?.codeId)}
                                                                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: '#e74c3c' }}
                                                                >
                                                                    <Trash2 size={16} />
                                                                </button>
                                                            </div>

                                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                                                <div>
                                                                    <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>
                                                                        Frais de Constat (FCFA)
                                                                    </label>
                                                                    <input
                                                                        type="number" min={0}
                                                                        value={dossier.fraisConstat || ''}
                                                                        onChange={(e) => mettreAJourMontant(dossier?.codeId, 'fraisConstat', e.target.value)}
                                                                        style={inputStyle()}
                                                                    />
                                                                </div>
                                                                <div>
                                                                    <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>
                                                                        Frais Accessoires (FCFA)
                                                                    </label>
                                                                    <input
                                                                        type="number" min={0}
                                                                        value={dossier.fraisAccessoires || ''}
                                                                        onChange={(e) => mettreAJourMontant(dossier?.codeId, 'fraisAccessoires', e.target.value)}
                                                                        style={inputStyle()}
                                                                    />
                                                                </div>
                                                            </div>

                                                            <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #E0E0E0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                <span style={{ fontSize: '13px', fontWeight: '600', color: '#333333' }}>
                                                                    Total : {total.toLocaleString('fr-FR')} FCFA
                                                                </span>
                                                                {estValide ? (
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#2ECC71' }}>
                                                                        <CheckCircle size={16} />
                                                                        <span style={{ fontSize: '12px' }}>Valide</span>
                                                                    </div>
                                                                ) : (
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#e74c3c' }}>
                                                                        <AlertCircle size={16} />
                                                                        <span style={{ fontSize: '12px' }}>Dépasse le SAP</span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })
                                            )}
                                        </div>

                                        {/* Récapitulatif */}
                                        {dossiersSelectionnes.length > 0 && (
                                            <>
                                                {/* Rappel du responsable dans le récap */}
                                                {responsable?.trim() ? (
                                                    <div style={{
                                                        display: 'flex', alignItems: 'center', gap: '8px',
                                                        padding: '10px 14px', borderRadius: '8px',
                                                        backgroundColor: '#F0FBF5', border: '1px solid #A8E6CF',
                                                        marginBottom: '12px', fontSize: '13px', color: '#145A32',
                                                    }}>
                                                        <User size={14} />
                                                        <span><strong>Responsable :</strong> {responsable.trim()}</span>
                                                    </div>
                                                ) : (
                                                    <div style={{
                                                        display: 'flex', alignItems: 'center', gap: '8px',
                                                        padding: '10px 14px', borderRadius: '8px',
                                                        backgroundColor: '#fff8f8', border: '1px solid #f5c6cb',
                                                        marginBottom: '12px', fontSize: '13px', color: '#e74c3c',
                                                    }}>
                                                        <AlertCircle size={14} />
                                                        Renseignez le responsable de l'entité (colonne gauche) pour pouvoir enregistrer.
                                                    </div>
                                                )}

                                                <div style={{ padding: '16px', backgroundColor: '#A8E6CF20', borderRadius: '8px', marginBottom: '16px' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                                                        <Calculator size={18} style={{ color: '#2ECC71' }} />
                                                        <span style={{ fontWeight: '600', color: '#333333' }}>Récapitulatif</span>
                                                    </div>

                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '14px' }}>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                            <span style={{ color: '#666' }}>Total Frais de Constat :</span>
                                                            <span style={{ fontWeight: '600', color: '#333333' }}>{totaux.totalFraisConstat.toLocaleString('fr-FR')} FCFA</span>
                                                        </div>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                            <span style={{ color: '#666' }}>Total Frais Accessoires :</span>
                                                            <span style={{ fontWeight: '600', color: '#333333' }}>{totaux.totalFraisAccessoires.toLocaleString('fr-FR')} FCFA</span>
                                                        </div>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '8px', borderTop: '2px solid #2ECC71', fontSize: '16px' }}>
                                                            <span style={{ fontWeight: '600', color: '#145A32' }}>Montant Net :</span>
                                                            <span style={{ fontWeight: '700', color: '#145A32' }}>{totaux.totalBrut.toLocaleString('fr-FR')} FCFA</span>
                                                        </div>
                                                    </div>
                                                </div>

                                                <button
                                                    onClick={enregistrerReglement}
                                                    disabled={saving || !validerReglement()}
                                                    style={{
                                                        width: '100%', padding: '14px',
                                                        backgroundColor: (saving || !validerReglement()) ? '#ccc' : '#2ECC71',
                                                        color: 'white', border: 'none', borderRadius: '8px',
                                                        fontSize: '16px', fontWeight: '600',
                                                        cursor: (saving || !validerReglement()) ? 'not-allowed' : 'pointer',
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        gap: '8px', transition: 'all 0.2s',
                                                    }}
                                                    onMouseEnter={(e) => { if (!saving && validerReglement()) e.currentTarget.style.backgroundColor = '#27AE60'; }}
                                                    onMouseLeave={(e) => { if (!saving && validerReglement()) e.currentTarget.style.backgroundColor = '#2ECC71'; }}
                                                >
                                                    <Save size={20} />
                                                    {saving ? "Enregistrement…" : "Enregistrer le Règlement"}
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <Footer />
                        <div className="content-backdrop fade"></div>
                    </div>
                </div>
            </div>

            <div className="layout-overlay layout-menu-toggle"></div>
            <div className="drag-target"></div>
        </div>
    );
};

export default GestionPaiementSinistreEntitesConstat;